/**
 * Lightweight i18n layer for Sourcity v3.
 *
 * Deliberately dependency-free: translations are plain JSON keyed by dotted
 * paths, so the same files can be handed to a translator or dropped into
 * i18next later without reshaping.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en.json';
import ar from './locales/ar.json';

export const LANGUAGES = ['en', 'ar'] as const;
export type Language = (typeof LANGUAGES)[number];

export type Direction = 'ltr' | 'rtl';

/** Everything a language needs to render itself in the switcher. */
export const LANGUAGE_META: Record<Language, { label: string; nativeLabel: string; dir: Direction; locale: string }> = {
  en: { label: 'English', nativeLabel: 'English', dir: 'ltr', locale: 'en-US' },
  ar: { label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl', locale: 'ar' },
};

type Dictionary = Record<string, unknown>;

const dictionaries: Record<Language, Dictionary> = { en, ar };

const STORAGE_KEY = 'sourcity_language';

function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/** Explicit links win, followed by the saved choice; first visits use Arabic. */
export function detectLanguage(): Language {
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  if (isLanguage(fromQuery)) return fromQuery;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // localStorage can throw in private / embedded contexts.
  }

  return 'ar';
}

export function applyDocumentLanguage(lang: Language): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = LANGUAGE_META[lang].dir;
  document.title = lang === 'ar' ? 'Sourcity AI - رفيقك الذكي للدراسة' : 'Sourcity AI - Your AI Study Companion';
  document.querySelector('meta[name="description"]')?.setAttribute('content', lang === 'ar'
    ? 'حوّل ملفات PDF إلى تجربة تعليمية تفاعلية مع اختبارات ذكية وملاحظات وتحويل النص إلى كلام.'
    : 'Transform your PDFs into interactive learning experiences with AI-powered quizzes, smart notes, and text-to-speech.');
}

/** Walk a dotted key (`profile.usage.title`) through a nested dictionary. */
function lookup(dict: Dictionary, key: string): string | undefined {
  const value = key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Dictionary)) {
      return (acc as Dictionary)[part];
    }
    return undefined;
  }, dict);
  return typeof value === 'string' ? value : undefined;
}

/** Replace `{{name}}` placeholders with the supplied values. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
}

export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;
/** Returns an array value (e.g. a feature bullet list) for the current language. */
export type TranslateListFn = (key: string) => string[];

interface I18nContextValue {
  lang: Language;
  dir: Direction;
  locale: string;
  isRTL: boolean;
  setLang: (lang: Language) => void;
  t: TranslateFn;
  tList: TranslateListFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    try {
      return detectLanguage();
    } catch {
      return 'ar';
    }
  });

  const meta = LANGUAGE_META[lang];

  // Keep the document in sync so CSS logical properties and screen readers
  // both pick up the direction change.
  useEffect(() => {
    applyDocumentLanguage(lang);
  }, [lang, meta.dir]);

  const setLang = useCallback((next: Language) => {
    if (!isLanguage(next)) return;
    setLangState(next);
    applyDocumentLanguage(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore storage failures — the in-memory choice still applies.
    }
    // Keep an explicit locale link consistent on reload, without changing
    // existing paths, payment-return parameters, fragments or auth routes.
    const url = new URL(window.location.href);
    if (url.searchParams.has('lang')) {
      url.searchParams.set('lang', next);
      window.history.replaceState(window.history.state, '', url);
    }
  }, []);

  const t = useCallback<TranslateFn>(
    (key, vars) => {
      const translated = lookup(dictionaries[lang], key) ?? lookup(dictionaries.en, key);
      if (translated === undefined) {
        if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
        return key;
      }
      return interpolate(translated, vars);
    },
    [lang]
  );

  const tList = useCallback<TranslateListFn>(
    (key) => {
      const read = (dict: Dictionary) => {
        const value = key.split('.').reduce<unknown>((acc, part) => {
          if (acc && typeof acc === 'object' && part in (acc as Dictionary)) {
            return (acc as Dictionary)[part];
          }
          return undefined;
        }, dict);
        return Array.isArray(value) ? (value as string[]) : undefined;
      };
      const list = read(dictionaries[lang]) ?? read(dictionaries.en);
      if (!list) {
        if (import.meta.env.DEV) console.warn(`[i18n] missing list key: ${key}`);
        return [];
      }
      return list;
    },
    [lang]
  );

  const value = useMemo<I18nContextValue>(
    () => ({ lang, dir: meta.dir, locale: meta.locale, isRTL: meta.dir === 'rtl', setLang, t, tList }),
    [lang, meta.dir, meta.locale, setLang, t, tList]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside a <LanguageProvider>');
  return ctx;
}

/** Convenience alias mirroring the react-i18next shape. */
export function useTranslation() {
  const { t, tList, lang, dir, locale, isRTL, setLang } = useI18n();
  return { t, tList, lang, dir, locale, isRTL, setLang };
}

/** Formats a date in the active locale (Arabic uses Latin digits for legibility). */
export function useLocaleFormat() {
  const { locale } = useI18n();
  return useMemo(
    () => ({
      date: (value: string | number | Date) =>
        new Date(value).toLocaleDateString(locale === 'ar' ? 'ar-u-nu-latn' : locale),
      dateTime: (value: string | number | Date) =>
        new Date(value).toLocaleString(locale === 'ar' ? 'ar-u-nu-latn' : locale),
      number: (value: number) => value.toLocaleString(locale === 'ar' ? 'ar-u-nu-latn' : locale),
    }),
    [locale]
  );
}
